import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Store, Users, Star, Gem, TrendingUp, Bell, BellOff, Globe, Phone, MapPin,
  BarChart3, ShoppingBag,
} from 'lucide-react';
import { api, compact, money, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import PostCard from '../components/PostCard';
import Composer from '../components/Composer';
import { Avatar, Stars, TierBadge, SkeletonList, Empty, SectionHead, Sheet } from '../components/ui';

export default function ShopPage() {
  const { slug } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState('offers');
  const [rateOpen, setRateOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');

  const { data: s, isLoading } = useQuery({ queryKey: ['shop', slug], queryFn: () => api.get(`/shops/${slug}`).then(r => r.data) });
  const { data: posts } = useQuery({ queryKey: ['feed', 'shop', slug], queryFn: () => api.get('/posts', { params: { shop: slug, limit: 25 } }).then(r => r.data), enabled: !!s });
  const { data: ratings } = useQuery({ queryKey: ['ratings', s?.id], queryFn: () => api.get(`/shops/${s.id}/ratings`).then(r => r.data), enabled: !!s && tab === 'reviews' });

  const followM = useMutation({
    mutationFn: () => api.post(`/shops/${s.id}/follow`),
    onSuccess: ({ data }) => { toast.success(data.following ? 'Following! You get 100% of their offers 🔔' : 'Unfollowed'); qc.invalidateQueries(['shop', slug]); },
  });

  const rateM = useMutation({
    mutationFn: () => api.post(`/shops/${s.id}/rate`, { stars, comment }),
    onSuccess: ({ data }) => {
      toast.success(data.awarded ? `Thanks! +${data.awarded} points ⭐` : 'Rating updated');
      setRateOpen(false); qc.invalidateQueries(['shop', slug]); useAuth.getState().load();
    },
    onError: e => toast.error(e.message),
  });

  if (isLoading || !s) return <SkeletonList n={3} h="h-40" />;
  const me = s.me;

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card overflow-hidden">
        <div className="h-28 bg-gradient-to-r from-mint-400 to-emerald-600 relative">
          {s.cover_url && <img src={s.cover_url} className="w-full h-full object-cover" alt="" />}
        </div>
        <div className="p-4 -mt-12">
          <div className="flex items-end gap-3">
            <div className="w-20 h-20 rounded-2xl bg-white shadow-lg ring-4 ring-white overflow-hidden shrink-0">
              {s.logo_url ? <img src={s.logo_url} className="w-full h-full object-cover" alt="" />
                : <div className="w-full h-full grid place-items-center bg-mint-100"><Store size={28} className="text-mint-600" /></div>}
            </div>
            <div className="flex-1 pb-1 min-w-0">
              <h1 className="font-display text-xl font-extrabold truncate">{s.name}</h1>
              <div className="flex items-center gap-2 flex-wrap">
                <Stars value={s.rating_avg} count={s.rating_count} size={12} />
                <span className="chip-ink">{s.kind.replace('_', ' ')}</span>
                {s.city_handle && <Link to={`/g/${encodeURIComponent(s.city_handle)}`} className="chip-brand">{s.city_handle}</Link>}
              </div>
            </div>
          </div>

          {s.description && <p className="text-sm text-ink-600 mt-3">{s.description}</p>}

          <div className="flex flex-wrap gap-3 mt-3 text-[11px] font-semibold text-ink-500">
            {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-brand-600"><Globe size={11} /> Website</a>}
            {s.phone && <a href={`tel:${s.phone}`} className="flex items-center gap-1"><Phone size={11} /> {s.phone}</a>}
            {s.address && <span className="flex items-center gap-1"><MapPin size={11} /> {s.address}</span>}
          </div>

          <div className="grid grid-cols-3 gap-2 mt-4">
            <Stat icon={Users} value={compact(s.stats.community)} label="Customers" />
            <Stat icon={ShoppingBag} value={compact(s.stats.purchases)} label="Orders" />
            <Stat icon={Gem} value={compact(s.stats.points_given)} label="Points given" />
          </div>

          {/* MY LOYALTY */}
          {me && (
            <div className="mt-4 p-3.5 rounded-2xl bg-gradient-to-r from-brand-50 to-amber-50 border border-brand-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black text-ink-500 uppercase tracking-wide">Your loyalty</span>
                <TierBadge tier={me.tier} label={me.tier_label} multiplier={me.multiplier} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="font-black text-sm">{money(me.total_spent, s.currency)}</p><p className="text-[10px] text-ink-400 font-bold">SPENT</p></div>
                <div><p className="font-black text-sm">{me.purchases_count}</p><p className="text-[10px] text-ink-400 font-bold">ORDERS</p></div>
                <div><p className="font-black text-sm text-brand-600">{Number(me.multiplier).toFixed(2)}×</p><p className="text-[10px] text-ink-400 font-bold">MULTIPLIER</p></div>
              </div>
              {me.next_tier && (
                <div className="mt-2.5">
                  <div className="flex justify-between text-[10px] font-bold text-ink-500 mb-1">
                    <span>Next: {me.next_tier.label} ({Number(me.next_tier.multiplier).toFixed(2)}×)</span>
                    <span>{money(me.next_tier.spent_needed, s.currency)} to go</span>
                  </div>
                  <div className="stat-bar">
                    <div className="stat-fill bg-gradient-to-r from-brand-400 to-brand-600"
                      style={{ width: `${Math.min(100, (Number(me.total_spent) / me.next_tier.min_spent) * 100)}%` }} />
                  </div>
                </div>)}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mt-3">
            <button onClick={() => followM.mutate()} className={me?.following ? 'btn-ghost' : 'btn-primary'}>
              {me?.following ? <><BellOff size={14} /> Following</> : <><Bell size={14} /> Follow</>}
            </button>
            {me?.is_owner
              ? <Link to={`/shop/${s.id}/dashboard`} className="btn-dark"><BarChart3 size={14} /> Dashboard</Link>
              : <button onClick={() => { setStars(me?.my_rating?.stars || 5); setComment(me?.my_rating?.comment || ''); setRateOpen(true); }}
                  className="btn-ghost"><Star size={14} /> {me?.my_rating ? 'Edit rating' : 'Rate +10💎'}</button>}
          </div>
        </div>
      </div>

      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100">
        {[['offers', 'Offers & posts'], ['reviews', `Reviews (${s.rating_count})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${tab === k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>{l}</button>))}
      </div>

      {tab === 'offers' && (
        <div className="space-y-3">
          {me?.is_owner && <Composer shopId={s.id} />}
          {!posts ? <SkeletonList n={3} h="h-48" />
            : !posts.length ? <Empty icon={Store} title="No offers yet" sub="Follow to get notified when they post." />
            : posts.map(p => <PostCard key={p.id} post={p} />)}
        </div>
      )}

      {tab === 'reviews' && (
        <div className="card p-2 space-y-1">
          {!ratings ? <SkeletonList n={4} h="h-16" />
            : !ratings.length ? <Empty icon={Star} title="No reviews yet" sub="Be the first to rate this shop." />
            : ratings.map(r => (
              <div key={r.id} className="p-3 rounded-xl hover:bg-ink-50">
                <div className="flex items-center gap-2.5">
                  <Avatar src={r.avatar_url} name={r.username} size={32} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{r.display_name || r.username}</p>
                    <Stars value={r.stars} size={11} />
                  </div>
                  <span className="text-[11px] text-ink-400 font-semibold">{timeAgo(r.created_at)}</span>
                </div>
                {r.comment && <p className="text-sm text-ink-600 mt-2 pl-10">{r.comment}</p>}
              </div>))}
        </div>
      )}

      <Sheet open={rateOpen} onClose={() => setRateOpen(false)} title={`Rate ${s.name}`}>
        <div className="text-center py-2">
          <Stars value={stars} size={38} interactive onChange={setStars} />
          <p className="text-sm font-bold text-ink-500 mt-2">
            {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent!'][stars]}
          </p>
        </div>
        <textarea className="input min-h-[90px] mt-3" placeholder="Tell the community about your experience…"
          value={comment} onChange={e => setComment(e.target.value)} />
        <button onClick={() => rateM.mutate()} disabled={rateM.isPending} className="btn-primary w-full mt-3">
          {rateM.isPending ? 'Saving…' : 'Submit rating'}
        </button>
      </Sheet>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-ink-50 rounded-xl p-2.5 text-center">
      <Icon size={15} className="mx-auto text-ink-400 mb-1" />
      <p className="font-black text-base">{value}</p>
      <p className="text-[10px] font-bold text-ink-400 uppercase">{label}</p>
    </div>
  );
}